// src/domains/billaddr.ts —— U-04 开卡表单·美国账单地址（产品 area=US；2026-10-15 用户反馈：州下拉 + 城市建议 + 邮编号段校验）
// 纯函数：表单迁移、提交前校验、视图值；数据表见 shared/src/us-address.ts（与后端 cardcore/usaddr.go 同源）。
// 城市控件选「下拉 + 其他手输」而非 <datalist>：Android WebView（Telegram Android）对 datalist 下拉支持不稳定，原生 <select> 两端都可用。
import { US_CITY_RE, US_STATES, ZIP5_RE, usState, usStateLabel, zipInState, zipRangeText } from '../../../shared/src/us-address';
import type { OpenForm } from './cards.types';
import { str } from './types';

type Dict = Readonly<Record<string, unknown>>;
type Lang = 'zh' | 'en';

// 城市下拉「其他（手动输入）」的哨兵值（不会是真实城市名：含下划线，过不了 US_CITY_RE）
export const CITY_OTHER = '__other__';

export const isUsArea = (area: string | null | undefined): boolean => (area ?? '').trim().toUpperCase() === 'US';

const rangeSep = (lang: Lang): string => (lang === 'zh' ? '、' : ', ');
const fill = (tpl: string, vars: Readonly<Record<string, string>>): string => tpl.replace(/\{(\w+)\}/g, (m, k: string) => vars[k] ?? m);

// 选州：已选城市在新州列表内或处于手输态则保留，否则清空（避免「California + Houston」残留）
export function pickState(f: OpenForm, code: string): OpenForm {
  const st = usState(code);
  const keep = f.cityManual || (!!st && st.cities.includes(f.city));
  return { ...f, state: st ? st.code : '', city: keep ? f.city : '' };
}

// 城市下拉：选「其他」进入手输态并清空城市，选列表项退出手输态
export function pickCity(f: OpenForm, v: string): OpenForm {
  return v === CITY_OTHER ? { ...f, cityManual: true, city: '' } : { ...f, cityManual: false, city: v };
}

// 邮编输入：只留数字、截 5 位
export const zipDigits = (v: string): string => v.replace(/\D/g, '').slice(0, 5);

export function zipMismatchText(t: Dict, code: string, lang: Lang): string {
  return fill(str(t, 'errUsZipState'), { state: code, ranges: zipRangeText(code, rangeSep(lang)) });
}

// 提交前校验（顺序同表单：街道 → 州 → 城市 → 邮编）；通过返回 null
export function usAddrError(t: Dict, f: OpenForm, lang: Lang): string | null {
  if (!f.street.trim()) return str(t, 'errAddr');
  const st = usState(f.state);
  if (!st) return str(t, 'errUsState');
  const city = f.city.trim();
  // 非手输态时城市须为下拉项（防切换卡种后残留的自由输入值在下拉里不可见却被提交）
  if (!city || (!f.cityManual && !st.cities.includes(city))) return str(t, 'errUsCity');
  if (!US_CITY_RE.test(city)) return str(t, 'errUsCityFmt');
  const zip = f.zip.trim();
  if (!ZIP5_RE.test(zip)) return str(t, 'errUsZip');
  if (!zipInState(st.code, zip)) return zipMismatchText(t, st.code, lang);
  return null;
}

// 服务端 4220 field=holder.* → 中文提示（前端校验漏网或 Bot / 旧客户端同口径兜底）
export function holderFieldError(t: Dict, field: string, f: OpenForm, lang: Lang): string | null {
  switch (field) {
    case 'holder.first':
    case 'holder.last':
      return str(t, 'errHolder');
    case 'holder.street':
      return str(t, 'errAddr');
    case 'holder.state':
      return str(t, 'errUsState');
    case 'holder.city':
      return str(t, 'errUsCityFmt');
    case 'holder.zip': {
      const st = usState(f.state);
      return st && ZIP5_RE.test(f.zip.trim()) ? zipMismatchText(t, st.code, lang) : str(t, 'errUsZip');
    }
    default:
      return null;
  }
}

const SELECT_STYLE =
  'height:46px;border:1px solid #dfe5e2;border-radius:12px;padding:0 10px;font-size:14px;outline:none;background:#fff;color:#0f1a17;width:100%;min-width:0;-webkit-appearance:menulist;appearance:menulist';
const INPUT_STYLE = 'height:46px;border:1px solid #dfe5e2;border-radius:12px;padding:0 14px;font-size:14px;outline:none;background:#fff';
const NOTE_STYLE = 'font-size:11px;color:#9aa5a1';
const ERR_COLOR = '#d1434b';

export interface Option {
  v: string;
  label: string;
}

export interface AddrView {
  addrLbl: { street: string; city: string; state: string; zip: string };
  addrPh: { zip: string; cityManual: string };
  usStateOptions: Option[];
  usCityOptions: Option[];
  usCityValue: string;
  usCityDisabled: boolean;
  usCityStyle: string;
  zipNote: string;
  zipNoteStyle: string;
  zipInputStyle: string;
}

export function addrView(t: Dict, f: OpenForm, us: boolean, lang: Lang): AddrView {
  if (!us) {
    // 非 US：保持自由输入，占位改为本地化字段名（原型字典 street / city / state / zip）
    return {
      addrLbl: { street: str(t, 'street'), city: str(t, 'city'), state: str(t, 'state'), zip: str(t, 'zip') },
      addrPh: { zip: '', cityManual: '' },
      usStateOptions: [], usCityOptions: [], usCityValue: '', usCityDisabled: true, usCityStyle: SELECT_STYLE,
      zipNote: '', zipNoteStyle: NOTE_STYLE, zipInputStyle: INPUT_STYLE,
    };
  }
  const st = usState(f.state);
  const zip = f.zip.trim();
  const mismatch = !!st && ZIP5_RE.test(zip) && !zipInState(st.code, zip);
  const first = st?.zips[0];
  const zipNote = mismatch
    ? zipMismatchText(t, st.code, lang)
    : st
      ? fill(str(t, 'zipHintState'), { state: st.code, ranges: zipRangeText(st.code, rangeSep(lang)) })
      : str(t, 'zipHintNoState');
  return {
    addrLbl: { street: str(t, 'addrStreet'), city: str(t, 'addrCity'), state: str(t, 'addrState'), zip: str(t, 'addrZip') },
    addrPh: { zip: first ? `${String(first[0]).padStart(3, '0')}01` : '00000', cityManual: str(t, 'phCityManual') },
    usStateOptions: [{ v: '', label: str(t, 'phState') }, ...US_STATES.map((s) => ({ v: s.code, label: usStateLabel(s) }))],
    usCityOptions: st
      ? [{ v: '', label: str(t, 'phCityPick') }, ...st.cities.map((c) => ({ v: c, label: c })), { v: CITY_OTHER, label: str(t, 'cityOther') }]
      : [{ v: '', label: str(t, 'cityNeedState') }],
    usCityValue: f.cityManual ? CITY_OTHER : st?.cities.includes(f.city) ? f.city : '',
    usCityDisabled: !st,
    usCityStyle: st ? SELECT_STYLE : `${SELECT_STYLE};background:#f4f6f5;color:#9aa5a1`,
    zipNote,
    zipNoteStyle: mismatch ? `font-size:11px;color:${ERR_COLOR}` : NOTE_STYLE,
    zipInputStyle: mismatch ? `${INPUT_STYLE};border-color:${ERR_COLOR}` : INPUT_STYLE,
  };
}
